import {
  FFS_RESULT_CASES_PART_4,
  FFS_RESULT_CASES_PART_5,
  FfsAnalysisResult,
  FfsPart,
  FfsPartFiveResultIds,
  FfsPartFourResultIds,
  FfsResultType,
  INDETERMINATE_FFS_RESULT_ID,
} from 'features/indicationTriage'
import {
  FfsLtaReportData,
  FfsLtaReportDataPartFive,
  FfsLtaReportDataPartFour,
  isPartFiveReport,
  isPartFourReport,
} from 'pages/reporting/reports/ffs/types'
import { PDF_COLORS } from 'pages/reporting/shared/pdfGeneration/constants/colors'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

// ----------------------------- Result Cases Accessors -----------------------------

export function getResultCasesForPart(
  ffsPart: FfsPart
): readonly FfsAnalysisResult[] {
  return ffsPart === FfsPart.PART_4
    ? FFS_RESULT_CASES_PART_4
    : FFS_RESULT_CASES_PART_5
}

function getResultCaseById(
  ffsPart: FfsPart,
  resultId: string
): FfsAnalysisResult {
  const cases = getResultCasesForPart(ffsPart)
  return (
    cases.find((c) => c.resultId === resultId) ?? getDefaultResultCase(ffsPart)
  )
}

function getDefaultResultCase(ffsPart: FfsPart): FfsAnalysisResult {
  const cases = getResultCasesForPart(ffsPart)
  return (
    cases.find((c) => c.resultId === INDETERMINATE_FFS_RESULT_ID) ?? cases[0]
  )
}

// ----------------------------- PDF Color Mapping -----------------------------

export function getPdfResultColor(resultType: FfsResultType): string {
  switch (resultType) {
    case FfsResultType.Pass:
      return PDF_COLORS.SUCCESS
    case FfsResultType.Derate:
      return PDF_COLORS.GECKO_ORANGE
    case FfsResultType.Fail:
      return PDF_COLORS.ERROR
    case FfsResultType.Pending:
    case FfsResultType.Skipped:
    default:
      return PDF_COLORS.TEXT_MUTED
  }
}

// ----------------------------- Helpers -----------------------------

function toNum(
  measurement: GeckoMeasurement | null | undefined,
  unit: GeckoMeasurementUnit
): number | null {
  if (!measurement) return null
  try {
    return measurement.convertTo(unit).num
  } catch {
    return null
  }
}

function toComparableNum(
  value: GeckoMeasurement | null | undefined,
  unitRef: GeckoMeasurement | null | undefined
): number | null {
  if (!value || !unitRef) return null
  try {
    return value.convertTo(unitRef.unit).num
  } catch {
    return null
  }
}

// ----------------------------- Part 5 Selection -----------------------------

/**
 * Common flaw size criteria check for Part 5 (same for Level 1 and Level 2)
 * Returns failing criteria names if any criteria fail, or empty array if all pass
 */
function checkPartFiveFlawSizeCriteriaInternal(
  report: FfsLtaReportDataPartFive
): string[] {
  const failingCriteria: string[] = []

  // 1) Remaining thickness ratio (rt) >= threshold (use stored threshold or default 0.2)
  const rtVal =
    toNum(report.flawSizeCriteria.rt, GeckoMeasurementUnit.Ratio) ??
    computeRemainingThicknessRatio(
      report.dataValues.tmm,
      report.userParams.fcaMl,
      report.dataValues.tc
    )
  const rtThreshold =
    toNum(
      report.flawSizeCriteria.remainingThicknessThresholdRatio,
      GeckoMeasurementUnit.Ratio
    ) ?? 0.2
  if (rtVal === null || rtVal < rtThreshold) {
    failingCriteria.push('Remaining thickness ratio')
  }

  // 2) Minimum thickness: (tmm - fcaMl) >= tmm_threshold
  const tmmVal = toNum(report.dataValues.tmm, GeckoMeasurementUnit.Millimeters)
  const fcaMlVal = toNum(
    report.userParams.fcaMl,
    GeckoMeasurementUnit.Millimeters
  )
  const tmmThreshold = toNum(
    report.flawSizeCriteria.tmm_threshold,
    GeckoMeasurementUnit.Millimeters
  )
  const minThickness =
    tmmVal !== null && fcaMlVal !== null ? tmmVal - fcaMlVal : null
  if (
    minThickness === null ||
    tmmThreshold === null ||
    minThickness < tmmThreshold
  ) {
    failingCriteria.push('Minimum thickness')
  }

  // 3) Location criteria (LMSD): lmsd >= lmsd_threshold (use stored threshold or calculate)
  const lmsdVal = toNum(
    report.flawSizeCriteria.lmsd,
    GeckoMeasurementUnit.Millimeters
  )
  const dVal = toNum(report.dataValues.d_ml, GeckoMeasurementUnit.Millimeters)
  const tcVal = toNum(report.dataValues.tc, GeckoMeasurementUnit.Millimeters)
  const lmsdThreshold =
    toNum(
      report.flawSizeCriteria.lmsd_threshold,
      GeckoMeasurementUnit.Millimeters
    ) ??
    (dVal !== null && tcVal !== null ? 1.8 * Math.sqrt(dVal * tcVal) : null)
  if (lmsdVal === null || lmsdThreshold === null || lmsdVal < lmsdThreshold) {
    failingCriteria.push('Location criteria (LMSD)')
  }

  return failingCriteria
}

/**
 * Part 5 Level 1 result selection
 * Uses MAWPr vs MAWP_design comparison for final acceptability
 */
function selectPartFiveLevelOneResult(
  report: FfsLtaReportDataPartFive
): FfsAnalysisResult {
  const ffsPart = FfsPart.PART_5

  // Check flaw size criteria (same as Level 2)
  const failingCriteria = checkPartFiveFlawSizeCriteriaInternal(report)
  if (failingCriteria.length > 0) {
    const base = getResultCaseById(ffsPart, FfsPartFiveResultIds.FAIL_FLAW_SIZE)
    return { ...base, failedCriteria: failingCriteria }
  }

  // Compare MAWPr vs Design MAWP
  const designMawp = report.mawp.mawp_design
  const mawpr = report.mawp.mawpr
  const mawprVal = toComparableNum(mawpr, designMawp)
  const designVal = designMawp?.num ?? null

  if (mawprVal === null || designVal === null) {
    return getResultCaseById(ffsPart, FfsPartFiveResultIds.FAIL_MISSING_DATA)
  }

  if (mawprVal < designVal) {
    return getResultCaseById(ffsPart, FfsPartFiveResultIds.DERATE)
  }

  // Pass path: determine high vs low RSF
  const rsfVal = toNum(
    report.finalAcceptabilityCriteria.rsf,
    GeckoMeasurementUnit.Ratio
  )
  if (rsfVal !== null && rsfVal > 0.9) {
    return getResultCaseById(ffsPart, FfsPartFiveResultIds.PASS_HIGH_RSF)
  }
  return getResultCaseById(ffsPart, FfsPartFiveResultIds.PASS_LOW_RSF)
}

/**
 * Part 5 Level 2 result selection (STEP 9 - Critical Thickness Profile)
 * Uses RSF vs RSF_a comparison for final acceptability:
 * - If RSF >= RSF_a: acceptable for operation at MAWP from STEP 7
 * - If RSF < RSF_a: derate to MAWPr (computed using Part 2, paragraph 2.4.2.2)
 */
function selectPartFiveLevelTwoResult(
  report: FfsLtaReportDataPartFive
): FfsAnalysisResult {
  const ffsPart = FfsPart.PART_5

  // Check flaw size criteria (same as Level 1)
  const failingCriteria = checkPartFiveFlawSizeCriteriaInternal(report)
  if (failingCriteria.length > 0) {
    const base = getResultCaseById(ffsPart, FfsPartFiveResultIds.FAIL_FLAW_SIZE)
    return { ...base, failedCriteria: failingCriteria }
  }

  // STEP 9: Compare RSF vs RSF_a (Critical Thickness Profile)
  const rsfVal = toNum(
    report.finalAcceptabilityCriteria.rsf,
    GeckoMeasurementUnit.Ratio
  )
  const rsfAVal = toNum(
    report.finalAcceptabilityCriteria.rsf_a,
    GeckoMeasurementUnit.Ratio
  )

  if (rsfVal === null || rsfAVal === null) {
    return getResultCaseById(ffsPart, FfsPartFiveResultIds.FAIL_MISSING_DATA)
  }

  // If RSF >= RSF_a: acceptable for operation at MAWP from STEP 7
  if (rsfVal >= rsfAVal) {
    // Pass path: determine high vs low RSF (same categorization as Level 1)
    if (rsfVal > 0.9) {
      return getResultCaseById(ffsPart, FfsPartFiveResultIds.PASS_HIGH_RSF)
    }
    return getResultCaseById(ffsPart, FfsPartFiveResultIds.PASS_LOW_RSF)
  }

  // If RSF < RSF_a: derate to MAWPr
  return getResultCaseById(ffsPart, FfsPartFiveResultIds.DERATE)
}

/**
 * Main Part 5 result selection - branches based on ffsLevel
 */
function selectPartFiveResult(
  report: FfsLtaReportDataPartFive
): FfsAnalysisResult {
  if (report.ffsLevel === '2') {
    return selectPartFiveLevelTwoResult(report)
  }
  // Default to Level 1 logic
  return selectPartFiveLevelOneResult(report)
}

function computeRemainingThicknessRatio(
  tmm: GeckoMeasurement | null,
  fcaMl: GeckoMeasurement | null,
  tc: GeckoMeasurement | null
): number | null {
  const tmmVal = toNum(tmm, GeckoMeasurementUnit.Millimeters)
  const fcaMlVal = toNum(fcaMl, GeckoMeasurementUnit.Millimeters)
  const tcVal = toNum(tc, GeckoMeasurementUnit.Millimeters)
  if (tmmVal === null || fcaMlVal === null || tcVal === null || tcVal === 0) {
    return null
  }
  return (tmmVal - fcaMlVal) / tcVal
}

// ----------------------------- Part 4 Selection -----------------------------

function selectPartFourResult(
  report: FfsLtaReportDataPartFour
): FfsAnalysisResult {
  const ffsPart = FfsPart.PART_4

  // Thickness criteria pass check
  const effMeasured = toNum(
    report.thicknessCriteria.effective_measured_thickness,
    GeckoMeasurementUnit.Millimeters
  )
  const thicknessCriteriaMin = toNum(
    report.thicknessCriteria.thickness_criteria_min,
    GeckoMeasurementUnit.Millimeters
  )

  const hasThicknessValues =
    effMeasured !== null && thicknessCriteriaMin !== null
  const thicknessPass =
    hasThicknessValues && effMeasured! >= thicknessCriteriaMin!

  // MAWP comparison
  const designMawp = report.mawp.mawp_design
  const mawpr = report.mawp.mawpr
  const mawprVal = toComparableNum(mawpr, designMawp)
  const designVal = designMawp?.num ?? null

  if (designVal === null || mawprVal === null) {
    return getResultCaseById(
      ffsPart,
      FfsPartFourResultIds.FAIL_MISSING_INFORMATION
    )
  }

  if (thicknessPass && mawprVal >= designVal) {
    return getResultCaseById(ffsPart, FfsPartFourResultIds.PASS)
  }

  if (!thicknessPass) {
    return getResultCaseById(
      ffsPart,
      FfsPartFourResultIds.FAIL_THICKNESS_CRITERIA
    )
  }

  if (mawprVal < designVal) {
    return getResultCaseById(ffsPart, FfsPartFourResultIds.FAIL_MAWP_CRITERIA)
  }

  return getDefaultResultCase(ffsPart)
}

// ----------------------------- Public API -----------------------------

export function selectResultCaseFromReport(
  ffsReport: FfsLtaReportData
): FfsAnalysisResult {
  if (isPartFiveReport(ffsReport)) {
    return selectPartFiveResult(ffsReport)
  }
  if (isPartFourReport(ffsReport)) {
    return selectPartFourResult(ffsReport)
  }
  // Fallback to indeterminate if part not recognized
  return getDefaultResultCase(FfsPart.PART_5)
}

// ----------------------------- Criteria Check Helpers -----------------------------

export interface MawpCriteriaResult {
  pass: boolean
  mawpr: GeckoMeasurement | null
  mawpDesign: GeckoMeasurement | null
  mawprFormatted: string
  mawpDesignFormatted: string
}

export interface ThicknessCriteriaResult {
  pass: boolean
  effectiveMeasuredThickness: GeckoMeasurement | null
  thicknessCriteriaMin: GeckoMeasurement | null
  effectiveMeasuredFormatted: string
  thicknessCriteriaMinFormatted: string
}

export interface FlawSizeCriteriaResult {
  overallPass: boolean
  checks: {
    remainingThicknessRatio: {
      pass: boolean
      value: number | null
      threshold: number
      label: string
    }
    minimumThickness: {
      pass: boolean
      value: number | null
      threshold: number | null
      label: string
    }
    lmsdCriteria: {
      pass: boolean
      value: number | null
      threshold: number | null
      label: string
    }
  }
}

export interface RsfCriteriaResult {
  pass: boolean
  rsf: GeckoMeasurement | null
  rsf_a: GeckoMeasurement | null
  rsfFormatted: string
  rsfAFormatted: string
}

function formatMeasurement(measurement: GeckoMeasurement | null): string {
  if (!measurement) return 'N/A'
  const num = measurement.num
  const formatted =
    Math.abs(num) < 0.01 || Math.abs(num) >= 10000
      ? num.toExponential(3)
      : num.toFixed(3).replace(/\.?0+$/, '')
  return `${formatted} ${measurement.unit.id}`
}

export function checkMawpCriteria(
  ffsReport: FfsLtaReportData
): MawpCriteriaResult {
  const mawpr = ffsReport.mawp.mawpr
  const mawpDesign = ffsReport.mawp.mawp_design

  const mawprVal = toComparableNum(mawpr, mawpDesign)
  const designVal = mawpDesign?.num ?? null

  const pass = mawprVal !== null && designVal !== null && mawprVal >= designVal

  return {
    pass,
    mawpr,
    mawpDesign,
    mawprFormatted: formatMeasurement(mawpr),
    mawpDesignFormatted: formatMeasurement(mawpDesign),
  }
}

export function checkThicknessCriteria(
  report: FfsLtaReportDataPartFour
): ThicknessCriteriaResult {
  const effectiveMeasuredThickness =
    report.thicknessCriteria.effective_measured_thickness
  const thicknessCriteriaMin = report.thicknessCriteria.thickness_criteria_min

  const effMeasured = toNum(
    effectiveMeasuredThickness,
    GeckoMeasurementUnit.Millimeters
  )
  const thicknessMin = toNum(
    thicknessCriteriaMin,
    GeckoMeasurementUnit.Millimeters
  )

  const pass =
    effMeasured !== null && thicknessMin !== null && effMeasured >= thicknessMin

  return {
    pass,
    effectiveMeasuredThickness,
    thicknessCriteriaMin,
    effectiveMeasuredFormatted: formatMeasurement(effectiveMeasuredThickness),
    thicknessCriteriaMinFormatted: formatMeasurement(thicknessCriteriaMin),
  }
}

export function checkFlawSizeCriteria(
  report: FfsLtaReportDataPartFive
): FlawSizeCriteriaResult {
  // 1) Remaining thickness ratio (rt) >= threshold (use stored threshold or default 0.2)
  const rtVal =
    toNum(report.flawSizeCriteria.rt, GeckoMeasurementUnit.Ratio) ??
    computeRemainingThicknessRatio(
      report.dataValues.tmm,
      report.userParams.fcaMl,
      report.dataValues.tc
    )
  const rtThreshold =
    toNum(
      report.flawSizeCriteria.remainingThicknessThresholdRatio,
      GeckoMeasurementUnit.Ratio
    ) ?? 0.2
  const rtPass = rtVal !== null && rtVal >= rtThreshold

  // 2) Minimum thickness: (tmm - fcaMl) >= tmm_threshold
  const tmmVal = toNum(report.dataValues.tmm, GeckoMeasurementUnit.Millimeters)
  const fcaMlVal = toNum(
    report.userParams.fcaMl,
    GeckoMeasurementUnit.Millimeters
  )
  const tmmThreshold = toNum(
    report.flawSizeCriteria.tmm_threshold,
    GeckoMeasurementUnit.Millimeters
  )
  const minThickness =
    tmmVal !== null && fcaMlVal !== null ? tmmVal - fcaMlVal : null
  const minThicknessPass =
    minThickness !== null &&
    tmmThreshold !== null &&
    minThickness >= tmmThreshold

  // 3) Location criteria (LMSD): lmsd >= lmsd_threshold (use stored threshold or calculate)
  const lmsdVal = toNum(
    report.flawSizeCriteria.lmsd,
    GeckoMeasurementUnit.Millimeters
  )
  const dVal = toNum(report.dataValues.d_ml, GeckoMeasurementUnit.Millimeters)
  const tcVal = toNum(report.dataValues.tc, GeckoMeasurementUnit.Millimeters)
  const lmsdThreshold =
    toNum(
      report.flawSizeCriteria.lmsd_threshold,
      GeckoMeasurementUnit.Millimeters
    ) ??
    (dVal !== null && tcVal !== null ? 1.8 * Math.sqrt(dVal * tcVal) : null)
  const lmsdPass =
    lmsdVal !== null && lmsdThreshold !== null && lmsdVal >= lmsdThreshold

  return {
    overallPass: rtPass && minThicknessPass && lmsdPass,
    checks: {
      remainingThicknessRatio: {
        pass: rtPass,
        value: rtVal,
        threshold: rtThreshold,
        label: 'Remaining Thickness Ratio (Rt)',
      },
      minimumThickness: {
        pass: minThicknessPass,
        value: minThickness,
        threshold: tmmThreshold,
        label: 'Minimum Thickness (tmm - FCA)',
      },
      lmsdCriteria: {
        pass: lmsdPass,
        value: lmsdVal,
        threshold: lmsdThreshold,
        label: 'Location Criteria (LMSD)',
      },
    },
  }
}

/**
 * Check RSF criteria for Part 5 Level 2 (STEP 9 - Critical Thickness Profile)
 * Compares RSF >= RSF_a to determine acceptability
 */
export function checkRsfCriteria(
  report: FfsLtaReportDataPartFive
): RsfCriteriaResult {
  const rsf = report.finalAcceptabilityCriteria.rsf
  const rsf_a = report.finalAcceptabilityCriteria.rsf_a

  const rsfVal = toNum(rsf, GeckoMeasurementUnit.Ratio)
  const rsfAVal = toNum(rsf_a, GeckoMeasurementUnit.Ratio)

  const pass = rsfVal !== null && rsfAVal !== null && rsfVal >= rsfAVal

  return {
    pass,
    rsf,
    rsf_a,
    rsfFormatted: formatMeasurement(rsf),
    rsfAFormatted: formatMeasurement(rsf_a),
  }
}
